'use client';

import { useId, useRef, useState } from 'react';

/**
 * In-place "Request a quote" dialog for brand / category pages.
 *
 * Those pages have no contact form of their own; navigating away to the
 * homepage form lost the product context. The trigger opens a native
 * <dialog> right on the page with the requested item pinned (non-editable)
 * and sent along to /api/send-email, so the enquiry always names the item.
 * English-only by design — brand/category page bodies are English (Guyana
 * market); nav/footer still localize.
 */
type Props = {
  /** What the visitor is asking about — pinned in the dialog and emailed. */
  item: string;
  label?: string;
  className?: string;
};

export default function QuoteDialog({ item, label = 'Request a quote', className = 'btn btn-primary' }: Props) {
  const uid = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = () => {
    setSent(false);
    setError(null);
    dialogRef.current?.showModal();
  };
  const close = () => dialogRef.current?.close();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      message: formData.get('message'),
      item,
    };

    // Same phone rule as the homepage contact form
    const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/;
    if (!phoneRegex.test(String(data.phone ?? ''))) {
      setError('Please enter a valid phone number.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.success) {
        setSent(true);
        (e.target as HTMLFormElement).reset();
      } else {
        setError(result.message || 'Error sending request. Please call us at +592 763-8844.');
      }
    } catch {
      setError('Sorry, there was an error. Please call us at +592 763-8844.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button type="button" className={className} onClick={open}>
        {label}
      </button>
      <dialog
        ref={dialogRef}
        className="quote-dialog"
        aria-label={`Request a quote — ${item}`}
        onClick={(e) => {
          if (e.target === dialogRef.current) close(); // backdrop click
        }}
      >
        <div className="quote-dialog-body contact-form">
          <button type="button" className="quote-dialog-close" onClick={close} aria-label="Close">
            &times;
          </button>

          {sent ? (
            <div className="quote-dialog-success">
              <h3>Request sent</h3>
              <p>
                Thanks — we&apos;ve received your quote request for <strong>{item}</strong> and will
                get back to you shortly.
              </p>
              <button type="button" className="btn btn-primary" onClick={close}>
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3>Request a quote</h3>
              <p className="quote-dialog-item">{item}</p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`${uid}-name`}>Full Name *</label>
                  <input type="text" id={`${uid}-name`} name="name" required placeholder="Your name" />
                </div>
                <div className="form-group">
                  <label htmlFor={`${uid}-phone`}>Phone *</label>
                  <input type="tel" id={`${uid}-phone`} name="phone" required placeholder="+592 ..." />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor={`${uid}-email`}>Email</label>
                <input type="email" id={`${uid}-email`} name="email" placeholder="you@company.com" />
              </div>
              <div className="form-group">
                <label htmlFor={`${uid}-message`}>Notes</label>
                <textarea
                  id={`${uid}-message`}
                  name="message"
                  rows={3}
                  placeholder="Site location, timeline, quantities…"
                />
              </div>
              <div className="form-group form-consent">
                <label className="consent-label">
                  <input type="checkbox" name="privacy-consent" required />
                  <span>
                    I agree to the{' '}
                    <a href="/privacy" target="_blank">privacy policy</a> and{' '}
                    <a href="/terms" target="_blank">terms</a>.
                  </span>
                </label>
              </div>
              {error && <p className="quote-dialog-error">{error}</p>}
              <div className="form-submit">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send request'}
                </button>
              </div>
            </form>
          )}
        </div>
      </dialog>
    </>
  );
}
